<template>
    <AuthLayout>
        <div class="flex h-screen">
            <Toast />

            <!-- Left Section: Image -->
            <div class="hidden lg:block lg:w-2/3">
                <img src="@/assets/left-side.jpg" alt="Welcome Image"
                    class="h-full w-full object-cover object-center welcome-image" />
            </div>

            <!-- Right Section: Login Form -->
            <div class="flex justify-center items-center w-full lg:w-1/3 logged-out-container px-4 sm:px-8">
                <!-- Background Image Container -->
                <div class="absolute inset-0 w-full h-full">
                    <img src="@/assets/right-side-background.png" alt="" class="w-full h-full object-cover "
                        loading="lazy" />
                </div>

                <!-- Content Container with glass effect -->
                <div class="flex flex-col justify-start items-center w-full max-w-md relative -mt-15">
                    <h1 class="welcome-back pb-10">
                        <span class="larger-letter">W</span>ELCOME <span class="larger-letter">B</span>ACK
                    </h1>
                    <div class=" p-8 rounded-lg w-full max-w-sm shadow-lg bg-[var(--secondary-background-color)]">
                        <div id="email-section-login" class="flex flex-col mt-2">
                            <label for="email" class="text-left w-full property-label">Email</label>
                            <IconField class="mt-1 w-full">
                                <InputIcon v-if="!form.email" class="pi pi-at" />
                                <InputText @keydown.enter="login" id="email" v-model="form.email" name="email" required
                                    autofocus class="w-full max-w-full" fluid />
                            </IconField>
                            <small class="error text-left w-full text-red-500 mt-1" v-if="errors.email">{{ errors.email
                                }}</small>
                        </div>

                        <div id="password-section-login" class="flex flex-col mt-7">
                            <label for="password" class="text-left w-full property-label">Password</label>
                            <div class="mt-1 w-full">
                                <Password @keydown.enter="login" id="password" v-model="form.password" name="password"
                                    placeholder="Password" required toggleMask :feedback="false"
                                    class="w-full max-w-full " fluid />
                            </div>
                            <small class="error text-left w-full text-red-500 mt-1" v-if="errors.password">{{
                                errors.password }}</small>
                        </div>

                        <div class="mt-5 w-full flex justify-center">
                            <Button :loading="isLoading" @click="login" :disabled="!isFormValid"
                                class="submit-button w-4/5" v-tooltip.bottom="!isFormValid ? buttonTooltip : ''"
                                severity="primary">
                                <span class="font-bolyar">
                                    <span class="larger-letter">L</span><span>OGIN</span>
                                </span>
                                <img src="@/assets/icons/login-icon.svg" alt="" class="w-5 h-5 ml-2" />
                            </Button>
                        </div>
                        <div class="mt-4 text-center mt-11 text-sm">
                            <p class="text-[var(--primary-text-color)]">
                                Don't have an account?
                            </p>
                            <router-link to="/register" class="text-blue-500 hover:underline">
                                Register
                            </router-link>
                            |
                            <a @click.prevent="forgotPasswordValidation"
                                class="text-blue-500 hover:underline cursor-pointer">
                                Forgot Password ?
                            </a>
                        </div>


                        <!-- Social Icons at bottom -->
                        <GetHelp container-class="mt-2 mb-5" />
                    </div>


                </div>
            </div>
        </div>
    </AuthLayout>
</template>


<script setup>
import AuthLayout from '@/views/AuthLayout.vue';

import AuthService from '@/services/AuthService'
import { reactive, ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { savedRoute } from '@/router';
import { useToast } from "primevue/usetoast";
import GetHelp from '@/components/GetHelp.vue';
const toast = useToast();
const store = useStore();
const router = useRouter();

const form = reactive({
    email: '',
    password: ''
});

const isLoading = ref(false)

const errors = reactive({
    email: null,
    password: null,
});

const isFormValid = computed(() => {
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    const isPasswordFilled = form.password.trim() !== '';

    return isEmailValid && isPasswordFilled;
});

const buttonTooltip = computed(() => {
    return errors.email || errors.password || 'Please fill a valid email and your password';
});

const validateForm = () => {
    errors.email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) ? null : 'Invalid email address.';
    // errors.password = form.password.length >= 6 ? null : 'Password must be at least 6 characters long.';
    return !errors.email && !errors.password;
};

const validateEmail = () => {
    errors.email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) ? null : 'Enter an email to be sent further instructions';
    return !errors.email;
}

const login = async () => {
    isLoading.value = true;

    try {
        if (validateForm()) {
            const response = await AuthService.login({ email: form.email, password: form.password });
            // Store the token in localStorage (or sessionStorage)
            localStorage.setItem('token', response.data.token);
            // Dispatch the user information to Vuex store
            // await store.dispatch('setUser', response.data.mappedUser);
            // Redirect to the home page
            if (savedRoute.value) {
                window.location.href = savedRoute.value;
                savedRoute.value = null; // Clear it after use
            } else {
                window.location.href = '/';
            }
        }
    } catch (err) {
        if (err.response && err.response.status === 401) {
            toast.add({
                severity: 'error',
                summary: 'Wrong email or pass',
                detail: 'The provided email and password don\'t match',
                life: 3000
            });
        }
        console.error('Error:', err);
    } finally {
        isLoading.value = false;
    }
};

const forgotPasswordValidation = async () => {
    if (validateEmail()) {
        try {
            await AuthService.forgotPassword(email.value);
            toast.add({
                severity: 'success',
                summary: 'Reset link sent',
                detail: 'Password reset link has been sent to your email.',
                life: 10000
            });
        } catch (error) {
            console.error('Error sending reset link:', error);
            message.value = error.response?.data?.message || 'Error sending reset link.';
        }

    }

}
</script>
<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.error {
    color: red;
    /* Error text color */
    font-size: 0.85rem;
    margin-top: 0.5rem;
}

.p-field {
    margin-bottom: 1em;
}

/* Optional: Add glass effect to content */
.backdrop-blur-sm {
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
}



.property-label {
    font-family: ProximaNova-Semibold;
    font-size: 15px;
    color: #FFFFFF;
}

.submit-button {
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>